import { useEffect, useState } from 'react';
import { Car, Flag, UsersRound } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { useHasActiveVehicles } from '@/hooks/useHasActiveVehicles';
import { useTripDialogs } from '@/hooks/useTripDialogs';
import { prepareTripStart } from '@/hooks/useVehicleStamp';
import { useMyVehicleTripsStore } from '@/store/useMyVehicleTripsStore';
import { formatDateTime, isoDate, parseUtcTimestamp } from '@/utils/dateUtils';
import { DRIVER } from '@/utils/vehicleTrips';
import { EndTripDialog } from './EndTripDialog';
import { TripStartDialog } from './TripStartDialog';

interface TripActionsProps {
  /** Clocked in right now — starting a trip without is allowed, the dialog says so. */
  clockedIn: boolean;
  /** `dark` on the dashboard hero, `light` everywhere else. */
  tone?: 'light' | 'dark';
}

const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
const DAY_TIME: Intl.DateTimeFormatOptions = { weekday: 'short', day: '2-digit', month: '2-digit', ...TIME };

/** "08:10", or "Mo, 02.11., 22:40" for a trip from an earlier day. */
function since(startedAt: string | undefined): string {
  const start = parseUtcTimestamp(startedAt);
  if (!start) return '';
  return formatDateTime(start, isoDate(start) === isoDate(new Date()) ? TIME : DAY_TIME);
}

/**
 * The trip menu (FAFD-68): the running trip ("Fahrt W 481 GL seit 08:10", "Mitfahrt …") with
 * "Fahrt beenden", otherwise "Fahrt starten" — independent of the clock state. Shows nothing
 * for a tenant without active vehicles. Owns its dialogs; Modal portals them to body.
 */
export function TripActions({ clockedIn, tone = 'light' }: TripActionsProps) {
  const { t } = useTranslation('vehicles');
  const current = useMyVehicleTripsStore((s) => s.current);
  const saving = useMyVehicleTripsStore((s) => s.saving);
  const fetchCurrent = useMyVehicleTripsStore((s) => s.fetchCurrent);
  const startTrip = useMyVehicleTripsStore((s) => s.startTrip);
  const endTrip = useMyVehicleTripsStore((s) => s.endTrip);
  const { askStart, askEnd, startDialog, endDialog } = useTripDialogs();
  const hasVehicles = useHasActiveVehicles();
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    void fetchCurrent();
  }, [fetchCurrent]);

  // why: a running trip stays visible even after its vehicle was deactivated — it must be endable.
  if (!current && !hasVehicles) return null;

  const start = async () => {
    setPreparing(true);
    const prepared = await prepareTripStart('start', isoDate(new Date())).finally(() => setPreparing(false));
    // Someone else's stamp may have started the trip meanwhile — then the menu just catches up.
    if (prepared.current) return;
    const answer = await askStart({
      mode: 'start',
      vehicles: prepared.vehicles,
      initial: prepared.initial,
      notClockedIn: !clockedIn,
    });
    if (answer?.trip) await startTrip(answer.trip);
  };

  const end = async () => {
    if (!current) return;
    const answer = await askEnd('end', current);
    if (answer) await endTrip(answer.endTrip ?? {});
  };

  const isRide = current !== null && current.role !== DRIVER;
  const Icon = isRide ? UsersRound : Car;
  const muted = tone === 'dark' ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className="space-y-1.5">
      {current && (
        <p className={`flex items-center gap-1.5 text-[12px] tabular-nums ${muted}`}>
          <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden />
          <span className="min-w-0 truncate">
            {t(isRide ? 'trip.statusRide' : 'trip.statusTrip', { plate: current.licensePlate, time: since(current.startedAt) })}
          </span>
        </p>
      )}
      <Button
        variant="secondary"
        size="md"
        className="w-full justify-center"
        onClick={current ? end : start}
        disabled={saving || preparing}
        leftIcon={current ? <Flag className="w-4 h-4" strokeWidth={2} /> : <Car className="w-4 h-4" strokeWidth={2} />}
      >
        {current ? t(isRide ? 'trip.endRide' : 'trip.end') : t('trip.start')}
      </Button>
      <TripStartDialog {...startDialog} busy={saving} />
      <EndTripDialog {...endDialog} busy={saving} />
    </div>
  );
}
